/* The size hero's raise is made to.
   A chart says which hands it raises; this says for how much. The number lives in the same
   place the trainer reads it from — `bets`, one key per bet in the line — so the box, the
   Raise button in a session and the chips in front of the seats are always the same amount.
   A size equal to what the table prices the raise at is not written down: a chart carries a
   size only where it was given one. */

import {
  blinds,
  heroRaiseBet,
  heroRaiseSize,
  pricedRaiseSize,
  type TableSpot,
} from "@preflop/core";
import { el } from "../../ui/dom";

/** What a box holds, as a number. Empty is the table's own price; anything else throws. */
export function raiseSizeOf(text: string): number | null {
  const t = (text ?? "").trim();
  if (!t) return null;
  const n = Number(t);
  if (!isFinite(n) || n <= 0 || n > 10000) throw new Error(`"${t}" is not a raise size`);
  return n;
}

/** The text a box opens on: the size in force here, or nothing where hero cannot raise. */
export function raiseSizeText(spot: TableSpot): string {
  const n = heroRaiseSize(spot);
  return n ? blinds(n) : "";
}

/** True where the two texts name the same size, empty and the table's price included. */
export function sameRaiseSize(spot: TableSpot, a: string, b: string): boolean {
  const read = (t: string): number | null => {
    try {
      return raiseSizeOf(t) ?? pricedRaiseSize(spot);
    } catch {
      return NaN;
    }
  };
  return read(a) === read(b);
}

/**
 * The box: a number under the Raise tag, stepping by the half blind. Null where there is
 * nothing to size — facing a jam, hero can only call it or fold.
 */
export function raiseSizeBox(
  spot: TableSpot,
  value: string,
  onInput: (text: string) => void,
): HTMLElement | null {
  if (!heroRaiseBet(spot.action, spot.branch)) return null;
  const priced = pricedRaiseSize(spot);
  const input = el("input", {
    type: "number",
    class: "size",
    min: "0.5",
    step: "0.5",
    value,
    ariaLabel: "Raise size in big blinds",
    dataset: { testid: "raise-size" },
    title:
      "What hero's raise is made to, in big blinds." +
      (priced ? ` Empty is the table's own price, ${blinds(priced)}.` : "") +
      // Hero's raise on an opening chart is the open itself, and the open size is also a
      // rung of the tree. A size typed here is what gets played; where the chart is filed
      // does not move with it.
      (spot.action === "RFI" ? " The chart stays filed under its open-size rung." : ""),
  });
  input.addEventListener("input", () => onInput(input.value));
  return input;
}
